import jsPDF from 'jspdf';
import * as echarts from 'echarts/core';
import {GridComponent, TooltipComponent, LegendComponent} from 'echarts/components';
import {LineChart} from 'echarts/charts';
import {CanvasRenderer} from 'echarts/renderers';

echarts.use([GridComponent, TooltipComponent, LegendComponent, LineChart, CanvasRenderer]);

type SeriesPoint = [number, number];

interface PdfElement {
    id: number;
    name: string;
    unit?: string;
    color: string;
    shell: string;
    sub_shell: string;
    source?: 'xrf' | 'minerals';
}

const unitSuffix = (unit?: string | null): string => (unit ? ` ${unit}` : '');

interface PdfDrillhole {
    holeId: string;
    tileImageDataUrl: string | null;
    activeLayer: string;
    seriesData: SeriesPoint[][];
}

export interface PdfExportParams {
    drillholes: PdfDrillhole[];
    elements: PdfElement[];
    xAxisMax: number;
    xAxisMin: number;
    elementMaxValues: Record<string, number>;
    elementMinValues: Record<string, number>;
    depthRange: [number, number] | null;
    combinedChart: boolean;
    combinedChartCount: number;
    elementChartIndex: Record<string, number>;
    chartMaxValues: Record<number, number>;
    chartMinValues: Record<number, number>;
    chartGroupMaxValues: number[];
}

const keyOf = (el: PdfElement): string => `${el.source ?? 'xrf'}_${el.id}`;

function getImageDimensions(dataUrl: string): Promise<{width: number; height: number}> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({width: img.naturalWidth, height: img.naturalHeight});
        img.onerror = reject;
        img.src = dataUrl;
    });
}

function renderChartOffscreen(option: echarts.EChartsCoreOption, width: number, height: number): string {
    const container = document.createElement('div');
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    document.body.appendChild(container);

    const chart = echarts.init(container);
    chart.setOption(option);
    const dataUrl = chart.getDataURL({type: 'jpeg', pixelRatio: 1.5, backgroundColor: '#fff'});
    chart.dispose();
    document.body.removeChild(container);
    return dataUrl;
}

function buildSingleChartOption(
    element: PdfElement,
    seriesData: SeriesPoint[],
    xMax: number,
    xMin: number,
    depthRange: [number, number] | null,
): echarts.EChartsCoreOption {
    return {
        animation: false,
        grid: {left: 55, right: 15, top: 35, bottom: 30},
        title: {
            text: `${element.name} (${element.shell}${element.sub_shell})`,
            textStyle: {fontSize: 11, color: '#333', fontWeight: 'bold'},
            left: 'center',
            top: 5,
        },
        xAxis: {
            type: 'value',
            name: element.unit ?? '',
            nameLocation: 'middle',
            nameGap: 22,
            nameTextStyle: {fontSize: 9, color: '#555'},
            min: xMin,
            max: xMax || 1,
            axisLabel: {
                fontSize: 9,
                color: '#555',
                formatter: (v: number) => (+v.toFixed(1)).toString().replace('.', ','),
            },
            splitLine: {show: true, lineStyle: {color: '#e0e0e0'}},
        },
        yAxis: {
            type: 'value',
            inverse: true,
            min: depthRange ? depthRange[0] : undefined,
            max: depthRange ? depthRange[1] : undefined,
            axisLabel: {
                fontSize: 9,
                color: '#555',
                formatter: (v: number) => Math.round(v).toString(),
            },
            splitLine: {show: true, lineStyle: {color: '#e0e0e0'}},
        },
        series: [{
            type: 'line',
            data: seriesData,
            lineStyle: {width: 1.5, color: element.color},
            itemStyle: {color: element.color},
            showSymbol: false,
            smooth: false,
        }],
    };
}

function buildCombinedChartOption(
    groupElements: {element: PdfElement; seriesData: SeriesPoint[]}[],
    xMax: number,
    xMin: number,
    depthRange: [number, number] | null,
    chartIndex: number,
): echarts.EChartsCoreOption {
    const title = groupElements.length <= 3
        ? groupElements.map(g => g.element.name).join(', ')
        : `Chart ${chartIndex + 1} (${groupElements.length} Elemente)`;

    // Show the unit on the axis only when every element in this chart shares it.
    const groupUnits = Array.from(new Set(groupElements.map(g => g.element.unit).filter(Boolean)));
    const axisUnit = groupUnits.length === 1 ? (groupUnits[0] as string) : '';

    return {
        animation: false,
        grid: {left: 55, right: 15, top: 40, bottom: 30},
        title: {
            text: title,
            textStyle: {fontSize: 10, color: '#333', fontWeight: 'bold'},
            left: 'center',
            top: 5,
        },
        legend: {
            show: true,
            top: 20,
            textStyle: {fontSize: 8, color: '#555'},
            data: groupElements.map(g => ({name: g.element.name, itemStyle: {color: g.element.color}})),
        },
        xAxis: {
            type: 'value',
            name: axisUnit,
            nameLocation: 'middle',
            nameGap: 22,
            nameTextStyle: {fontSize: 9, color: '#555'},
            min: xMin,
            max: xMax || 1,
            axisLabel: {
                fontSize: 9,
                color: '#555',
                formatter: (v: number) => (+v.toFixed(1)).toString().replace('.', ','),
            },
            splitLine: {show: true, lineStyle: {color: '#e0e0e0'}},
        },
        yAxis: {
            type: 'value',
            inverse: true,
            min: depthRange ? depthRange[0] : undefined,
            max: depthRange ? depthRange[1] : undefined,
            axisLabel: {
                fontSize: 9,
                color: '#555',
                formatter: (v: number) => Math.round(v).toString(),
            },
            splitLine: {show: true, lineStyle: {color: '#e0e0e0'}},
        },
        series: groupElements.map(g => ({
            type: 'line' as const,
            name: g.element.name,
            data: g.seriesData,
            lineStyle: {width: 1.5, color: g.element.color},
            itemStyle: {color: g.element.color},
            showSymbol: false,
            smooth: false,
        })),
    };
}

export async function exportCoreViewerPdf(params: PdfExportParams): Promise<void> {
    const {drillholes, elements, xAxisMax, xAxisMin, elementMaxValues, elementMinValues, depthRange, combinedChart, combinedChartCount, elementChartIndex, chartMaxValues, chartMinValues, chartGroupMaxValues} = params;

    // A4 landscape for better chart layout
    const pdf = new jsPDF({orientation: 'landscape', unit: 'mm', format: 'a4', compress: true});
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentW = pageW - 2 * margin;

    // ─── Title page ───
    pdf.setFontSize(18);
    pdf.text('CoreViewer Export', pageW / 2, 25, {align: 'center'});

    pdf.setFontSize(10);
    pdf.setTextColor(100);
    pdf.text(`Datum: ${new Date().toLocaleDateString('de-DE')}`, margin, 40);
    pdf.text(`Bohrlöcher: ${drillholes.map(d => d.holeId).join(', ')}`, margin, 47);
    pdf.text(`Image Layer: ${drillholes[0]?.activeLayer ?? '-'}`, margin, 54);
    pdf.text(`Aktive Elemente: ${elements.length}`, margin, 61);

    // Element legend with color squares
    let legendY = 72;
    const legendColWidth = contentW / 4;
    pdf.setFontSize(11);
    pdf.setTextColor(0);
    pdf.text('Aktive Elemente:', margin, legendY);
    legendY += 6;

    pdf.setFontSize(8);
    elements.forEach((el, i) => {
        const col = i % 4;
        const row = Math.floor(i / 4);
        const x = margin + col * legendColWidth;
        const y = legendY + row * 6;

        if (y > pageH - margin) return; // safety: don't overflow

        // Color square
        pdf.setFillColor(el.color);
        pdf.rect(x, y - 3, 3, 3, 'F');

        // Element name
        pdf.setTextColor(50);
        pdf.text(`${el.name} (${el.shell}${el.sub_shell})`, x + 5, y);
    });

    // ─── Per drillhole pages ───
    const chartAspect = 0.7; // width / height ratio for each chart cell

    for (const dh of drillholes) {
        pdf.addPage();

        // Header
        pdf.setFontSize(14);
        pdf.setTextColor(0);
        pdf.text(`Bohrloch: ${dh.holeId}`, margin, margin + 6);

        pdf.setFontSize(9);
        pdf.setTextColor(80);
        pdf.text(`Image Layer: ${dh.activeLayer}`, margin, margin + 13);

        const contentStartY = margin + 18;

        // ── Tile page: full dedicated page ──
        const hasChartData = elements.length > 0;
        const depthScaleW = !hasChartData && depthRange ? 12 : 0; // mm reserved for depth scale
        const tileAvailW = contentW - depthScaleW;
        const tileAvailH = pageH - contentStartY - margin;

        if (dh.tileImageDataUrl) {
            try {
                const dims = await getImageDimensions(dh.tileImageDataUrl);
                const imgAspect = dims.width / dims.height;
                let tileDrawW: number, tileDrawH: number;
                if (imgAspect > tileAvailW / tileAvailH) {
                    tileDrawW = tileAvailW;
                    tileDrawH = tileAvailW / imgAspect;
                } else {
                    tileDrawH = tileAvailH;
                    tileDrawW = tileAvailH * imgAspect;
                }
                const tileX = margin + depthScaleW + (tileAvailW - tileDrawW) / 2;
                pdf.addImage(dh.tileImageDataUrl, 'JPEG', tileX, contentStartY, tileDrawW, tileDrawH);

                // Draw depth scale next to tile when no charts are present
                if (depthScaleW > 0 && depthRange) {
                    const scaleX = margin + depthScaleW - 1;
                    const scaleTop = contentStartY;
                    const scaleH = tileDrawH;
                    pdf.setDrawColor(120);
                    pdf.setLineWidth(0.3);
                    pdf.line(scaleX, scaleTop, scaleX, scaleTop + scaleH);
                    const [dMin, dMax] = depthRange;
                    const tickCount = Math.min(10, Math.max(2, Math.floor(scaleH / 8)));
                    pdf.setFontSize(6);
                    pdf.setTextColor(80);
                    for (let t = 0; t <= tickCount; t++) {
                        const frac = t / tickCount;
                        const y = scaleTop + frac * scaleH;
                        const depth = dMin + frac * (dMax - dMin);
                        pdf.line(scaleX - 1.5, y, scaleX, y);
                        pdf.text(`${depth.toFixed(1)}m`, scaleX - 2, y + 1.2, {align: 'right'});
                    }
                }
            } catch {
                pdf.setFontSize(9);
                pdf.setTextColor(150);
                pdf.text('Tile-Bild nicht verfügbar', margin + 2, contentStartY + 10);
            }
        } else {
            pdf.setFontSize(9);
            pdf.setTextColor(150);
            pdf.text('Kein Tile-Bild', margin + 2, contentStartY + 10);
        }

        // ── Build chart images (skip when no data sources selected) ──
        const chartImages: string[] = [];

        if (!hasChartData) {
            // No charts to render — skip
        } else if (combinedChart) {
            const count = Math.max(1, combinedChartCount);
            const groups: {element: PdfElement; seriesData: SeriesPoint[]}[][] = Array.from({length: count}, () => []);
            elements.forEach((el, eIdx) => {
                const groupIdx = Math.min((elementChartIndex[keyOf(el)] ?? 1) - 1, count - 1);
                groups[groupIdx].push({element: el, seriesData: dh.seriesData[eIdx] ?? []});
            });

            for (let gi = 0; gi < groups.length; gi++) {
                if (groups[gi].length === 0) continue;
                const xMax = (chartMaxValues[gi] ?? (count > 1 ? chartGroupMaxValues[gi] : xAxisMax)) || 1;
                const xMin = chartMinValues[gi] ?? xAxisMin;
                const option = buildCombinedChartOption(groups[gi], xMax, xMin, depthRange, gi);
                chartImages.push(renderChartOffscreen(option, 400, 560));
            }
        } else {
            for (let eIdx = 0; eIdx < elements.length; eIdx++) {
                const el = elements[eIdx];
                const seriesData = dh.seriesData[eIdx] ?? [];
                const xMax = elementMaxValues[keyOf(el)] ?? xAxisMax;
                const xMin = elementMinValues[keyOf(el)] ?? xAxisMin;
                const option = buildSingleChartOption(el, seriesData, xMax, xMin, depthRange);
                chartImages.push(renderChartOffscreen(option, 400, 560));
            }
        }

        // ── Chart pages: 5 charts per page, full height, full width ──
        const chartsPerPage = 5;
        let placed = 0;

        while (placed < chartImages.length) {
            pdf.addPage();
            pdf.setFontSize(11);
            pdf.setTextColor(0);
            pdf.text(`${dh.holeId} — Charts`, margin, margin + 6);

            const chartY = margin + 12;
            const chartH = pageH - chartY - margin;
            const pageCount = Math.min(chartImages.length - placed, chartsPerPage);
            const chartW = contentW / pageCount;

            for (let i = 0; i < pageCount; i++) {
                pdf.addImage(chartImages[placed], 'JPEG', margin + i * chartW, chartY, chartW, chartH);
                placed++;
            }
        }
    }

    // Download
    pdf.save(`CoreViewer_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// Build series data from drillhole measurement data — mirrors DrillholeRow logic.
// Reads XRF-tagged elements from dri_mea and Minerals-tagged elements from
// dri_mea_minerals so combined multi-source views export correctly.
export function buildSeriesData(drillhole: any, elements: PdfElement[]): SeriesPoint[][] {
    const result: SeriesPoint[][] = elements.map(() => []);
    if (!drillhole) return result;

    const fill = (measurements: any[] | undefined, source: 'xrf' | 'minerals') => {
        if (!measurements) return;
        const indexMap = new Map<number, number>();
        elements.forEach((el, idx) => {
            if ((el.source ?? 'xrf') === source) indexMap.set(el.id, idx);
        });
        if (indexMap.size === 0) return;
        for (const mea of measurements) {
            for (const xrf of mea.mea_xrfres ?? []) {
                for (const spe of xrf.xrfres_spe ?? []) {
                    const depth = spe.position * -1;
                    for (const speres of spe.spe_speres ?? []) {
                        const idx = indexMap.get(speres.element_id);
                        if (idx !== undefined) result[idx].push([speres.value, depth]);
                    }
                }
            }
        }
    };

    fill(drillhole.dri_mea, 'xrf');
    fill(drillhole.dri_mea_minerals, 'minerals');

    for (const series of result) series.sort((a, b) => a[1] - b[1]);
    return result;
}
